import React, { useId } from 'react';
import { cn } from '@/utils/cn';
import { FieldShell, describedBy } from '@/components/UI/Field';
import { fieldClass } from '@/components/UI/fieldStyles';
import { ChevronDownIcon } from '@/components/UI/icons';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  /** Classes for the outer wrapper. `className` goes to the <select> itself. */
  wrapperClassName?: string;
}

/**
 * Native <select> (so mobile pickers, keyboard type-ahead and screen readers all keep working) dressed as the
 * RoofVox dropdown: pill field, chevron on the right. For searchable lists use SearchableSelect.
 */
const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, wrapperClassName, label, error, hint, id, required, children, ...props }, ref) => {
    const autoId = useId();
    const selectId = id ?? autoId;

    return (
      <FieldShell id={selectId} label={label} hint={hint} error={error} required={required} className={wrapperClassName}>
        <div className="relative">
          <select
            id={selectId}
            ref={ref}
            required={required}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy(selectId, hint, error)}
            className={cn(fieldClass({ error: Boolean(error) }), 'appearance-none pr-10', className)}
            {...props}
          >
            {children}
          </select>
          <ChevronDownIcon
            size={16}
            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-icon"
          />
        </div>
      </FieldShell>
    );
  },
);

Select.displayName = 'Select';

export { Select };
